/**
 * US business days, as the server counts them (server/api-util/business-days.js,
 * which decides every deadline): weekends and federal holidays don't count,
 * observed on the nearest weekday when they fall on a weekend. Days are
 * calendar days in US Eastern time.
 *
 * The order page uses this only to show a range the server would agree with
 * (businessDays.parity.test.js runs both on the same dates).
 */
const ZONE = 'America/New_York';

/** 'YYYY-MM-DD' of an instant, in US Eastern time. */
const easternDate = date =>
  new Intl.DateTimeFormat('en-CA', { timeZone: ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);

const iso = (y, m, d) => `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const weekday = (y, m, d) => new Date(Date.UTC(y, m - 1, d)).getUTCDay();

/** The nth (1-based) given weekday of a month; n = -1 is the last. */
const nthWeekday = (y, m, dow, n) => {
  if (n > 0) {
    const first = weekday(y, m, 1);
    return 1 + ((dow - first + 7) % 7) + (n - 1) * 7;
  }
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return last - ((weekday(y, m, last) - dow + 7) % 7);
};

/** A fixed-date holiday moves to Friday when on a Saturday, Monday when on a Sunday. */
const observed = (y, m, d) => {
  const dow = weekday(y, m, d);
  const shift = dow === 6 ? -1 : dow === 0 ? 1 : 0;
  const date = new Date(Date.UTC(y, m - 1, d + shift));
  return iso(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
};

const holidaysCache = new Map();

/** The federal holidays observed in a year, as 'YYYY-MM-DD'. */
export const federalHolidays = year => {
  if (holidaysCache.has(year)) return holidaysCache.get(year);
  const days = new Set([
    observed(year, 1, 1), // New Year's Day
    iso(year, 1, nthWeekday(year, 1, 1, 3)), // Martin Luther King Jr. Day
    iso(year, 2, nthWeekday(year, 2, 1, 3)), // Washington's Birthday
    iso(year, 5, nthWeekday(year, 5, 1, -1)), // Memorial Day
    observed(year, 6, 19), // Juneteenth
    observed(year, 7, 4), // Independence Day
    iso(year, 9, nthWeekday(year, 9, 1, 1)), // Labor Day
    iso(year, 10, nthWeekday(year, 10, 1, 2)), // Columbus Day
    observed(year, 11, 11), // Veterans Day
    iso(year, 11, nthWeekday(year, 11, 4, 4)), // Thanksgiving
    observed(year, 12, 25), // Christmas Day
  ]);
  // Next year's New Year's Day observed on Friday Dec 31.
  const nextNewYear = observed(year + 1, 1, 1);
  if (nextNewYear.startsWith(String(year))) days.add(nextNewYear);
  holidaysCache.set(year, days);
  return days;
};

// Eastern wall-clock time of an instant: its calendar date and time of day
const PARTS = new Intl.DateTimeFormat('en-US', {
  timeZone: ZONE,
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});
const easternParts = date => {
  const p = Object.fromEntries(PARTS.formatToParts(date).map(part => [part.type, part.value]));
  return { y: +p.year, m: +p.month, d: +p.day, h: +p.hour % 24, mi: +p.minute, s: +p.second };
};

// How far Eastern time is from UTC at an instant (it changes with daylight saving)
const offsetAt = date => {
  const p = easternParts(date);
  return Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi, p.s) - (date.getTime() - date.getUTCMilliseconds());
};

// The instant of an Eastern wall-clock time (read twice, across a clock change)
const easternInstant = ({ y, m, d, h, mi, s }, ms) => {
  const asUtc = Date.UTC(y, m - 1, d, h, mi, s, ms);
  const first = asUtc - offsetAt(new Date(asUtc));
  return new Date(asUtc - offsetAt(new Date(first)));
};

const isBusinessDate = (y, m, d) => {
  const dow = weekday(y, m, d);
  return dow !== 0 && dow !== 6 && !federalHolidays(y).has(iso(y, m, d));
};

export const isBusinessDay = date => {
  const day = easternDate(date);
  const [y, m, d] = day.split('-').map(Number);
  const dow = weekday(y, m, d);
  return dow !== 0 && dow !== 6 && !federalHolidays(y).has(day);
};

/**
 * The moment `n` business days after `from`: the same Eastern time of day, on the nth
 * business day after the day it started. Days are stepped on the calendar, so a clock
 * change (when a day has 23 or 25 hours) never moves a deadline by an hour.
 */
export const addBusinessDays = (from, n) => {
  const start = new Date(from);
  const time = easternParts(start);
  let { y, m, d } = time;
  let left = n;
  while (left > 0) {
    const next = new Date(Date.UTC(y, m - 1, d + 1));
    [y, m, d] = [next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate()];
    if (isBusinessDate(y, m, d)) left -= 1;
  }
  return easternInstant({ ...time, y, m, d }, start.getUTCMilliseconds());
};
